import { cn } from "@/lib/utils";
import RadioCircle from "@/components/ui/RadioCircle";

export type RoleCard = {
  id: string;
  name: string;
  description?: string | null;
};

type Props = {
  roles: RoleCard[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabledIds?: Set<string>;
  children?: React.ReactNode; // extra trailing card(s), e.g. onboarding's "Other"
};

// Role radio-cards with descriptions — shared by the onboarding "What do you
// do?" step and the admin assign-role dialog so both feel identical.
export default function RoleCardList({ roles, selectedId, onSelect, disabledIds, children }: Props) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {roles.map((r) => {
        const active = r.id === selectedId;
        const disabled = disabledIds?.has(r.id) ?? false;
        return (
          <button
            key={r.id}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onSelect(r.id)}
            className={cn(
              "flex items-start gap-3 rounded-md border px-4 py-3 text-left transition-colors",
              active ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-muted/50",
              disabled && "pointer-events-none opacity-50"
            )}
          >
            <span className="mt-0.5">
              <RadioCircle active={active} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{r.name}</span>
              {r.description && <span className="block text-xs text-muted-foreground">{r.description}</span>}
            </span>
          </button>
        );
      })}
      {children}
    </div>
  );
}
